import {
  decodeClozeFreetextAnswers,
  encodeClozeFreetextAnswers,
} from "@/lib/freetextQuizStatus";
import type { CompletionQuizQuestionContent } from "@/types/courseCompletionQuiz";

/** What a learner has put in so far, in the shape of the question's input. */
export type AnswerDraft =
  | { kind: "choice"; selected: string[] }
  | { kind: "boolean"; value: boolean | null }
  | { kind: "gaps"; values: string[] }
  | { kind: "pairs"; pairs: { firstPart: string; secondPart: string }[] }
  | { kind: "text"; text: string };

/** The type the server grades a question by. */
export const validationType = (subType: string): string =>
  subType.toLowerCase();

const gapCount = (content: CompletionQuizQuestionContent): number =>
  content.gaps?.length ?? 0;

export const emptyDraft = (
  content: CompletionQuizQuestionContent
): AnswerDraft => {
  switch (content.subType) {
    case "MULTIPLE_CHOICE":
    case "CODE_MULTIPLE_CHOICE":
      return { kind: "choice", selected: [] };
    case "TRUE_FALSE":
    case "CODE_TRUE_FALSE":
      return { kind: "boolean", value: null };
    case "CLOZE_DROPDOWN":
    case "CLOZE_FREETEXT":
      return { kind: "gaps", values: Array(gapCount(content)).fill("") };
    case "MATCHING_DEFINITION":
    case "MATCHING_SENTENCE":
      return { kind: "pairs", pairs: [] };
    default:
      return { kind: "text", text: "" };
  }
};

/** The `userInput` the server expects, the same as the lessons send. */
export const encodeAnswer = (
  content: CompletionQuizQuestionContent,
  draft: AnswerDraft
): string => {
  switch (draft.kind) {
    case "choice":
      return JSON.stringify(draft.selected);
    case "boolean":
      return String(draft.value);
    case "gaps":
      return content.subType === "CLOZE_FREETEXT"
        ? encodeClozeFreetextAnswers(draft.values)
        : JSON.stringify(draft.values);
    case "pairs":
      return JSON.stringify(draft.pairs);
    case "text":
      return draft.text;
  }
};

const parseJson = (input: string): unknown => {
  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
};

const asStrings = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : null;

/** The draft a stored answer stands for; empty when it cannot be read. */
export const decodeAnswer = (
  content: CompletionQuizQuestionContent,
  userInput: string
): AnswerDraft => {
  const empty = emptyDraft(content);

  switch (empty.kind) {
    case "choice": {
      const selected = asStrings(parseJson(userInput));
      return selected ? { kind: "choice", selected } : empty;
    }
    case "boolean":
      return userInput === "true" || userInput === "false"
        ? { kind: "boolean", value: userInput === "true" }
        : empty;
    case "gaps": {
      if (content.subType === "CLOZE_FREETEXT") {
        return {
          kind: "gaps",
          values: decodeClozeFreetextAnswers(userInput, gapCount(content)),
        };
      }
      const values = asStrings(parseJson(userInput));
      return values ? { kind: "gaps", values } : empty;
    }
    case "pairs": {
      const parsed = parseJson(userInput);
      const valid =
        Array.isArray(parsed) &&
        parsed.every(
          (pair) =>
            pair &&
            typeof pair.firstPart === "string" &&
            typeof pair.secondPart === "string"
        );
      return valid ? { kind: "pairs", pairs: parsed } : empty;
    }
    case "text":
      return { kind: "text", text: userInput };
  }
};

/** Whether the learner has said enough to confirm the answer. */
export const isDraftComplete = (
  content: CompletionQuizQuestionContent,
  draft: AnswerDraft
): boolean => {
  switch (draft.kind) {
    case "choice":
      return draft.selected.length >= Math.max(content.selectCount ?? 1, 1);
    case "boolean":
      return draft.value !== null;
    case "gaps":
      return (
        draft.values.length > 0 &&
        draft.values.every((value) => value.trim() !== "")
      );
    case "pairs":
      return (
        draft.pairs.length > 0 &&
        draft.pairs.length === (content.firstParts?.length ?? 0)
      );
    case "text":
      return draft.text.trim() !== "";
  }
};
